/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { IconButton, Paper, Stack, styled, Typography } from "@mui/material";
import { FC, useMemo, useRef, useState } from "react";
import { JobNodeInput } from "components/Common/JobNodeInput";
import {
  MaterialInput,
  MaterialInputLabel,
} from "components/Common/MaterialInput";
import DeleteIcon from "@mui/icons-material/Delete";
import { UserSelector } from "components/Common/UserSelector";
import { useJobName } from "hooks/GetJobNameHook";
import { RequestedEndDatetimesInput } from "./RequestedEndDatetimesInput";
import { useTranslation } from "react-i18next";
import { TaskControlMode } from "types";

export interface JobInputItem {
  jobId: number;
  materialType: number | undefined;
  materialQuantity: number | undefined;
  loadingNodeId: number | undefined;
  extensionTimeLoad: number | undefined;
  loadingUserId: string | undefined;
  unloadingNodeId: number | undefined;
  extensionTimeUnload: number | undefined;
  unloadingUserId: string | undefined;
  requestedEndDatetimeFrom: Date | undefined;
  requestedEndDatetimeTo: Date | undefined;
  mode: TaskControlMode;
}

/**
 * Props of JobInputCard
 *
 * @interface JobInputCardProps
 * @typedef {JobInputCardProps}
 */
interface JobInputCardProps {
  /**
   * Contents of job
   *
   * @type {JobInputItem}
   */
  job: JobInputItem;

  /**
   * The card is active
   *
   * @type {boolean}
   */
  active: boolean;

  /**
   * User can delete the card
   *
   * @type {?boolean}
   */
  deletable?: boolean;

  /**
   * Callback for delete button clicked
   *
   * @type {?(jobId: number) => void}
   */
  onDelete?: (jobId: number) => void;

  /**
   * Callback for contents edited
   *
   * @type {?(job: JobInputItem) => void}
   */
  onEdit?: (job: JobInputItem) => void;

  /**
   * Callback for card clicked
   *
   * @type {?(job: JobInputItem) => void}
   */
  onClick?: (jobId: number) => void;
}

export const JobInputCard: FC<JobInputCardProps> = ({
  job,
  active,
  deletable,
  onDelete,
  onEdit,
  onClick,
}) => {
  const { t } = useTranslation();
  const getJobName = useJobName();
  const [jobName, setJobName] = useState(
    getJobName(job.materialType, job.requestedEndDatetimeTo)
  );
  const jobInputRef = useRef({ ...job });
  const isEdit = useMemo(() => job.mode === "edit", [job.mode]);
  const materialLabel: MaterialInputLabel = useMemo(() => {
    return {
      id: t("AWVUI-MSG204"),
      quantity: t("AWVUI-MSG205"),
    };
  }, [t]);
  return (
    <JobCardPaper
      elevation={3}
      onClick={() => {
        onClick?.(jobInputRef.current.jobId);
      }}
      active={active}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          padding="4px"
        >
          <Typography variant="h6">{jobName}</Typography>
          <IconButton
            disabled={deletable !== undefined ? !deletable : false}
            onClick={() => {
              onDelete?.(job.jobId);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
        <MaterialInput
          label={materialLabel}
          disabled={isEdit}
          material={
            job.materialType !== undefined && job.materialQuantity !== undefined
              ? { id: job.materialType, quantity: job.materialQuantity }
              : undefined
          }
          onSelected={(materialId) => {
            jobInputRef.current.materialType = materialId;
            setJobName(getJobName(materialId, job.requestedEndDatetimeTo));
            onEdit?.(jobInputRef.current);
          }}
          onChangeQuantity={(materialQuantity) => {
            jobInputRef.current.materialQuantity = materialQuantity;
            onEdit?.(jobInputRef.current);
          }}
        />
        <JobNodeInput
          label={t("AWVUI-MSG206")}
          disabled={isEdit}
          jobNodeInput={{
            nodeId: job.loadingNodeId,
            interval: job.extensionTimeLoad,
          }}
          onSelected={(point) => {
            jobInputRef.current.loadingNodeId = point.nodeId;
            onEdit?.(jobInputRef.current);
          }}
        />
        <UserSelector
          label={t("AWVUI-MSG207")}
          disabled={isEdit}
          userId={
            job.loadingUserId !== undefined ? job.loadingUserId : undefined
          }
          onSelected={(loadingUserId) => {
            jobInputRef.current.loadingUserId = loadingUserId;
            onEdit?.(jobInputRef.current);
          }}
        />
        <JobNodeInput
          label={t("AWVUI-MSG208")}
          disabled={isEdit}
          jobNodeInput={{
            nodeId: job.unloadingNodeId,
            interval: job.extensionTimeUnload,
          }}
          onSelected={(point) => {
            jobInputRef.current.unloadingNodeId = point.nodeId;
            onEdit?.(jobInputRef.current);
          }}
        />
        <UserSelector
          label={t("AWVUI-MSG209")}
          disabled={isEdit}
          userId={
            job.unloadingUserId !== undefined ? job.unloadingUserId : undefined
          }
          onSelected={(unloadingUserId) => {
            jobInputRef.current.unloadingUserId = unloadingUserId;
            onEdit?.(jobInputRef.current);
          }}
        />
        <RequestedEndDatetimesInput
          requestedEndDatetimes={
            job.requestedEndDatetimeFrom !== undefined &&
            job.requestedEndDatetimeTo !== undefined
              ? {
                  from: job.requestedEndDatetimeFrom,
                  to: job.requestedEndDatetimeTo,
                }
              : undefined
          }
          onChange={(from, to) => {
            jobInputRef.current.requestedEndDatetimeFrom = from;
            jobInputRef.current.requestedEndDatetimeTo = to;
            setJobName(getJobName(jobInputRef.current.materialType, to));
            onEdit?.(jobInputRef.current);
          }}
        />
      </Stack>
    </JobCardPaper>
  );
};

interface JobInputPaperProps {
  active: boolean;
}
const JobCardPaper = styled(Paper)<JobInputPaperProps>(({ theme, active }) => ({
  padding: "10px",
  borderColor: theme.palette.primary.main,
  borderStyle: active ? "solid" : "hidden",
}));
